import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";

import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { EmailBody, EmailBodyProps } from "@mapmycustomers/ui";

import CreateEmailContext from "@app/component/email/creation/CreateEmailContext";
import messages from "@app/component/email/creation/messages";
import { downloadAttachedFile, loadImageToEmailBody } from "@app/store/email/actions";
import useAnalytics from "@app/util/contexts/useAnalytics";

type CreateEmailBodyProps = Pick<EmailBodyProps, "action" | "footer"> & typeof mapDispatchToProps;

const CreateEmailBody: React.FC<CreateEmailBodyProps> = ({
  action,
  footer,
  onDownloadAttachedFile,
  onLoadImageToEmailBody,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const {
    attachedFiles,
    attachFile,
    body,
    deleteAttachedFile,
    dynamicVarVariants,
    emailFilled,
    recipients,
    recipientType,
    setBody,
    setBodyLimitReached,
    setTemplateChanged,
    setUnsubscribeLinkIncluded,
    showPreview,
    template,
    unsubscribeLinkIncluded,
  } = useNonNullableContext(CreateEmailContext);

  const [bodyImageLoading, setBodyImageLoading] = useState<boolean>(false);
  const [processedTemplateId, setProcessedTemplateId] = useState<EmailTemplate["id"] | undefined>();

  const handleAction = useCallback(
    (analyticKey: string) => {
      analytics.clicked([analyticKey]);
    },
    [analytics]
  );

  const handleLoadImage = useCallback(
    (file: Blob, callback: (url: string) => void) => {
      setBodyImageLoading(true);
      onLoadImageToEmailBody({
        callback: (url) => {
          callback(url);
          setBodyImageLoading(false);
        },
        file,
      });
    },
    [onLoadImageToEmailBody, setBodyImageLoading]
  );

  const handleFilesSelect = useCallback(
    (file: File) => {
      attachFile({
        id: uuidv4(),
        preparedFile: file,
      });
    },
    [attachFile]
  );

  const handleChangeBody = useCallback(
    (text: string, limitReached: boolean, appliedTemplate?: EmailTemplate) => {
      setBody(text);
      setBodyLimitReached(limitReached);
      if (appliedTemplate && appliedTemplate.id === processedTemplateId) {
        setTemplateChanged(true);
      }
      setProcessedTemplateId(appliedTemplate?.id);
    },
    [setTemplateChanged, setBody, setBodyLimitReached, processedTemplateId, setProcessedTemplateId]
  );

  return (
    <EmailBody
      action={action}
      appliedTemplate={template}
      attachedFiles={attachedFiles}
      dynamicVarVariants={dynamicVarVariants}
      editorId="bulk-email-editor"
      entityType={recipientType}
      footer={footer}
      loading={bodyImageLoading}
      onAction={handleAction}
      onChange={handleChangeBody}
      onDeleteFile={deleteAttachedFile}
      onDownloadFile={(attachedFile) => onDownloadAttachedFile({ attachedFile })}
      onFilesSelect={handleFilesSelect}
      onLoadFile={handleLoadImage}
      onPreview={showPreview}
      onPreviewFile={(attachedFile) => onDownloadAttachedFile({ attachedFile, preview: true })}
      onUnsubscribeLinkIncludedChange={setUnsubscribeLinkIncluded}
      previewDisableText={emailFilled ? undefined : intl.formatMessage(messages.previewDisabled)}
      recipientCount={recipients.length}
      showActions
      unsubscribeLinkIncluded={unsubscribeLinkIncluded}
      value={body}
    />
  );
};

const mapDispatchToProps = {
  onDownloadAttachedFile: downloadAttachedFile,
  onLoadImageToEmailBody: loadImageToEmailBody,
};

export default connect(null, mapDispatchToProps)(CreateEmailBody);
